import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormInputError from "components/General/FormInputError";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";
import { useDynamicSecurityQuestionsOptions } from "hooks/useDynamicSecurityQuestionsOptions";

const StepThree = ({
  agentRegistrationFormData,
  setAgentRegistrationFormData,
  setCurrentStep,
  setConfirmationPassCode,
  setLoading,
}) => {
  const uuid = uuidv4();
  const { t } = useTranslation();

  const {
    setSecurityQuestionOneId,
    securityQuestionsOne,

    setSecurityQuestionTwoId,
    securityQuestionsTwo,

    setSecurityQuestionThreeId,
    securityQuestionsThree,
  } = useDynamicSecurityQuestionsOptions();

  const {
    register: registrationStepThreeRegister,
    getValues,
    handleSubmit,
    formState: { errors: registrationStepThreeErrors },
  } = useForm({
    defaultValues: agentRegistrationFormData,
  });

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  async function onSubmit(data) {
    let passcode = randomNumberInRange(100000, 999999);
    setConfirmationPassCode(passcode.toString());
    setLoading(true);
    let securitycode = randomNumberInRange(100000, 999999);
    let sec =
      parseInt(securitycode.toString().substring(3, 4)) === 0
        ? 1
        : parseInt(securitycode.toString().substring(3, 4));
    let confirmationcode = passcode * sec;

    let contentDictionary = {};
    contentDictionary.confirmationcode = confirmationcode.toString();
    contentDictionary.username = data.firstname;
    contentDictionary.securitycode = securitycode.toString();

    await sendEmailNotification(data.email, 12, contentDictionary)
      .then((response) => {
        setLoading(false);
        setCurrentStep(4);
        setAgentRegistrationFormData((curr) => ({ ...curr, ...data }));
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  }

  return (
    <form
      className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-medium"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="mt-4">
        <label id={`${uuid}-question1Label`}>
          {t("AgentRegister-step3-question-label-1")}
        </label>
        <select
          id={`${uuid}-question1`}
          className="w-full"
          {...registrationStepThreeRegister("securityQuestionOne", {
            required: {
              value: true,
              message: t("AgentRegister-step3-question-requiredMessage"),
            },
            onChange: (e) => setSecurityQuestionOneId(e.target.value),
          })}
          aria-labelledby={`${uuid}-question1Label`}
        >
          <option value="">
            {t("AgentRegister-step3-question-selectSecurityQuestionText")}
          </option>
          {securityQuestionsOne.map((question) => (
            <option
              key={`security question - ${question.id}`}
              value={question.id}
            >
              {question.question}
            </option>
          ))}
        </select>

        {registrationStepThreeErrors.securityQuestionOne && (
          <FormInputError
            id={`${uuid}-question1Error`}
            errorMessage={
              registrationStepThreeErrors.securityQuestionOne.message
            }
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-answer1Label`}>
          {t("AgentRegister-step3-answer-label-1")}
        </label>
        <input
          id={`${uuid}-answer1`}
          className="w-full"
          type="text"
          {...registrationStepThreeRegister("securtityAnswerOne", {
            required: {
              value: true,
              message: t("AgentRegister-step3-answer-requiredMessage"),
            },
            validate: {
              answerRuleBySelectedQuestion: (v) => {
                switch (getValues("securityQuestionOne")) {
                  case "6":
                    return (
                      (/^\d{4}$/.test(v) &&
                        parseInt(v) <= new Date().getFullYear()) ||
                      t("AgentRegister-step3-answer-fourDigitError")
                    );
                  case "8":
                    if (
                      v.split("/").length !== 2 ||
                      v.split("/")[0].length !== 2 ||
                      v.split("/")[1].length !== 2
                    )
                      return t(
                        "AgentRegister-step3-answer-incorrectDateFormatError"
                      );
                    if (
                      parseInt(v.split("/")[0]) < 1 ||
                      parseInt(v.split("/")[0]) > 12 ||
                      parseInt(v.split("/")[1]) < 1 ||
                      parseInt(v.split("/")[1]) > 31
                    )
                      return t("AgentRegister-step3-answer-validDOBError");
                    break;
                  case "14":
                    return (
                      /^\d{10}$/.test(v) ||
                      t("AgentRegister-step3-answer-validPhoneNumberError")
                    );
                  default:
                    return (
                      /^[A-Za-z ]+$/.test(v) ||
                      t("AgentRegister-step3-answer-alphabetError")
                    );
                }
              },
            },
            maxLength: {
              value: 100,
              message: t("AgentRegister-step3-answer-maxLengthError"),
            },
          })}
          aria-labelledby={`${uuid}-answer1Label`}
        />

        {registrationStepThreeErrors.securtityAnswerOne && (
          <FormInputError
            id={`${uuid}-question1Error`}
            errorMessage={
              registrationStepThreeErrors.securtityAnswerOne.message
            }
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-question2Label`}>
          {t("AgentRegister-step3-question-label-2")}
        </label>
        <select
          id={`${uuid}-question2`}
          className="w-full"
          {...registrationStepThreeRegister("securityQuestionTwo", {
            required: {
              value: true,
              message: t("AgentRegister-step3-question-requiredMessage"),
            },
            onChange: (e) => setSecurityQuestionTwoId(e.target.value),
          })}
          aria-labelledby={`${uuid}-question2Label`}
        >
          <option value="">
            {t("AgentRegister-step3-question-selectSecurityQuestionText")}
          </option>
          {securityQuestionsTwo.map((question) => (
            <option
              key={`security question - ${question.id}`}
              value={question.id}
            >
              {question.question}
            </option>
          ))}
        </select>

        {registrationStepThreeErrors.securityQuestionTwo && (
          <FormInputError
            id={`${uuid}-question2Error`}
            errorMessage={
              registrationStepThreeErrors.securityQuestionTwo.message
            }
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-answer2Label`}>
          {t("AgentRegister-step3-answer-label-2")}
        </label>
        <input
          id={`${uuid}-answer2`}
          className="w-full"
          type="text"
          {...registrationStepThreeRegister("securtityAnswerTwo", {
            required: {
              value: true,
              message: t("AgentRegister-step3-answer-requiredMessage"),
            },
            validate: {
              answerRuleBySelectedQuestion: (v) => {
                switch (getValues("securityQuestionTwo")) {
                  case "6":
                    return (
                      (/^\d{4}$/.test(v) &&
                        parseInt(v) <= new Date().getFullYear()) ||
                      t("AgentRegister-step3-answer-fourDigitError")
                    );
                  case "8":
                    if (
                      v.split("/").length !== 2 ||
                      v.split("/")[0].length !== 2 ||
                      v.split("/")[1].length !== 2
                    )
                      return t(
                        "AgentRegister-step3-answer-incorrectDateFormatError"
                      );
                    if (
                      parseInt(v.split("/")[0]) < 1 ||
                      parseInt(v.split("/")[0]) > 12 ||
                      parseInt(v.split("/")[1]) < 1 ||
                      parseInt(v.split("/")[1]) > 31
                    )
                      return t("AgentRegister-step3-answer-validDOBError");
                    break;
                  case "14":
                    return (
                      /^\d{10}$/.test(v) ||
                      t("AgentRegister-step3-answer-validPhoneNumberError")
                    );
                  default:
                    return (
                      /^[A-Za-z ]+$/.test(v) ||
                      t("AgentRegister-step3-answer-alphabetError")
                    );
                }
              },
            },
            maxLength: {
              value: 100,
              message: t("AgentRegister-step3-answer-maxLengthError"),
            },
          })}
          aria-labelledby={`${uuid}-answer2Label`}
        />

        {registrationStepThreeErrors.securtityAnswerTwo && (
          <FormInputError
            id={`${uuid}-question2Error`}
            errorMessage={
              registrationStepThreeErrors.securtityAnswerTwo.message
            }
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-question3Label`}>
          {t("AgentRegister-step3-question-label-3")}
        </label>
        <select
          id={`${uuid}-question3`}
          className="w-full"
          {...registrationStepThreeRegister("securityQuestionThree", {
            required: {
              value: true,
              message: t("AgentRegister-step3-question-requiredMessage"),
            },
            onChange: (e) => setSecurityQuestionThreeId(e.target.value),
          })}
          aria-labelledby={`${uuid}-question3Label`}
        >
          <option value="">
            {t("AgentRegister-step3-question-selectSecurityQuestionText")}
          </option>
          {securityQuestionsThree.map((question) => (
            <option
              key={`security question - ${question.id}`}
              value={question.id}
            >
              {question.question}
            </option>
          ))}
        </select>

        {registrationStepThreeErrors.securityQuestionThree && (
          <FormInputError
            id={`${uuid}-question3Error`}
            errorMessage={
              registrationStepThreeErrors.securityQuestionThree.message
            }
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-answer3Label`}>
          {t("AgentRegister-step3-answer-label-3")}
        </label>
        <input
          id={`${uuid}-answer3`}
          className="w-full"
          type="text"
          {...registrationStepThreeRegister("securtityAnswerThree", {
            required: {
              value: true,
              message: t("AgentRegister-step3-answer-requiredMessage"),
            },
            validate: {
              answerRuleBySelectedQuestion: (v) => {
                switch (getValues("securityQuestionThree")) {
                  case "6":
                    return (
                      (/^\d{4}$/.test(v) &&
                        parseInt(v) <= new Date().getFullYear()) ||
                      t("AgentRegister-step3-answer-fourDigitError")
                    );
                  case "8":
                    if (
                      v.split("/").length !== 2 ||
                      v.split("/")[0].length !== 2 ||
                      v.split("/")[1].length !== 2
                    )
                      return t(
                        "AgentRegister-step3-answer-incorrectDateFormatError"
                      );
                    if (
                      parseInt(v.split("/")[0]) < 1 ||
                      parseInt(v.split("/")[0]) > 12 ||
                      parseInt(v.split("/")[1]) < 1 ||
                      parseInt(v.split("/")[1]) > 31
                    )
                      return t("AgentRegister-step3-answer-validDOBError");
                    break;
                  case "14":
                    return (
                      /^\d{10}$/.test(v) ||
                      t("AgentRegister-step3-answer-validPhoneNumberError")
                    );
                  default:
                    return (
                      /^[A-Za-z ]+$/.test(v) ||
                      t("AgentRegister-step3-answer-alphabetError")
                    );
                }
              },
            },
            maxLength: {
              value: 100,
              message: t("AgentRegister-step3-answer-maxLengthError"),
            },
          })}
          aria-labelledby={`${uuid}-answer3Label`}
        />
        {registrationStepThreeErrors.securtityAnswerThree && (
          <FormInputError
            id={`${uuid}-answer3Error`}
            errorMessage={
              registrationStepThreeErrors.securtityAnswerThree.message
            }
          />
        )}
      </div>

      <div className="mt-4 text-right">
        <button
          type="button"
          className="ui-btn-secondary ui-btn-min-w mt-2"
          onClick={() => setCurrentStep(2)}
        >
          {t("AgentRegister-step3-previousButton")}
          <span className="sr-only">
            {t("AgentRegister-step3-previousButton-srText")}
          </span>
        </button>
        <button type="submit" className="ui-btn-primary ui-btn-min-w mt-2">
          {t("AgentRegister-step3-submitButton")}
          <span className="sr-only">
            {t("AgentRegister-step3-submitButton-srText")}
          </span>
        </button>
      </div>
    </form>
  );
};

export default StepThree;
